/**
 * A per-scope, per-browser mirror of `DataState`, so the next time this
 * project (or "All projects") is opened on this device the list paints from
 * what was last seen instead of a blank skeleton — and so the tables that
 * grow with how much work a project has ever done (`tasks`, `task_prs`, the
 * comment counts) can be read incrementally afterwards: a `gt` on the one
 * column standing for "changed since" rather than the whole table.
 *
 * IndexedDB, not `localStorage`: a few hundred tasks' `prompt`/`links`/
 * `images` easily clears the ~5MB a synchronous store budgets for a whole
 * origin, and every operation here is already async on the caller's side
 * (`RiggProvider`'s `load()` is a network read), so there is nothing to lose
 * by using the store built for this.
 *
 * Every table here is either never hard-deleted from the app's own write
 * paths (`tasks` — see the trash migration's comment, "used to be a hard
 * delete" — and `task_prs`, which cascades only off a task row that is never
 * itself removed) or append-only (`task_comments`, counted rather than
 * stored). That is what makes "only ask for rows newer than the cursor, and
 * merge them into what is already held" correct: nothing this module reads
 * incrementally can go missing between one read and the next without a row
 * changing to say so.
 */
import type { DataState } from './store.ts';

/**
 * Every table worth restoring instantly on a repeat visit — everything in
 * `DataState` except the session-only bits (`loaded`, `live`) and the
 * on-demand thread state (`comments`, `events`), which is read fresh per task
 * rather than cached, and would otherwise go stale silently for a task
 * nobody has reopened since. Questions and follow-ups are left out too: both
 * are small, read whole on every load, and a cache written before they
 * existed would otherwise restore a state without them.
 */
export type CachedState = Omit<
  DataState,
  'loaded' | 'live' | 'comments' | 'events' | 'questions' | 'followUps'
>;

export function emptyCachedState(): CachedState {
  return {
    tasks: {},
    categories: {},
    agents: {},
    subscriptions: {},
    repos: {},
    members: {},
    invites: {},
    intakes: {},
    profiles: {},
    settings: {},
    installations: {},
    installationRepos: [],
    ciRuns: {},
    taskPrs: {},
    commentCounts: {},
  };
}

/**
 * How far each incrementally-read table has gotten — the newest timestamp of
 * a row already folded into the `CachedState` sitting next to it. `null`
 * means "never read on this device", which is what tells `RiggProvider` to
 * ask for the whole table rather than "newer than nothing".
 */
export interface SyncCursors {
  tasks: string | null;
  taskPrs: string | null;
  /** Newest `created_at` of a `task_comments` row already counted — comments
   *  have no `updated_at` (append-only, so nothing about an existing row
   *  needs re-reading), so counting is what the cursor tracks. */
  commentCounts: string | null;
}

export function emptySyncCursors(): SyncCursors {
  return { tasks: null, taskPrs: null, commentCounts: null };
}

/**
 * The newest timestamp between `current` and `rows` — `null` only when both
 * are empty. Pure and total, so it is the same arithmetic whether the caller
 * is folding in a first full read or a later, empty delta.
 */
export function advanceCursor(current: string | null, rows: readonly string[]): string | null {
  let latest = current;
  for (const ts of rows) {
    if (latest === null || ts > latest) latest = ts;
  }
  return latest;
}

export interface CacheEntry {
  version: number;
  savedAt: string;
  cursors: SyncCursors;
  data: CachedState;
}

/**
 * Bumped whenever `CachedState`'s shape changes in a way a stale entry could
 * violate — a new required field, say. A version mismatch is treated exactly
 * like no entry at all: a full read, not a crash.
 */
// 2: `subscriptions` joined the cached tables.
// 3: tasks gained `kind`, `origin`, `origin_ref` and `quote` — a cached row
//    has none, and the incremental read would never fetch it again.
const CACHE_VERSION = 3;
const DB_NAME = 'rigg-cache';
const DB_VERSION = 1;
const STORE_NAME = 'scopes';

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

/**
 * The cached tables and cursors for one scope (`${userId}:${project or
 * "all"}`), or `null` on a first visit, a version bump, private browsing that
 * refuses IndexedDB, or anything else going wrong. Every caller treats that
 * the same as "nothing cached yet" — this is a warm start, never a source of
 * truth, so failing open (read Postgres in full) is always the safe fallback.
 */
export async function readScopeCache(key: string): Promise<CacheEntry | null> {
  try {
    const db = await openDb();
    if (!db) return null;
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => {
        const entry = req.result as CacheEntry | undefined;
        resolve(entry && entry.version === CACHE_VERSION ? entry : null);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/**
 * Best-effort — a quota error, a blocked upgrade (another tab holding an
 * older version open) or anything else here means the next visit reads
 * Postgres in full instead of from a warm cache, never a reason to fail the
 * load this was only trying to speed up.
 */
export async function writeScopeCache(
  key: string,
  entry: Omit<CacheEntry, 'version' | 'savedAt'>,
): Promise<void> {
  try {
    const db = await openDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(
        { ...entry, version: CACHE_VERSION, savedAt: new Date().toISOString() },
        key,
      );
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    // See the doc comment above — never surfaced, never retried.
  }
}
